import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './Forms.css';

export default function StudentLifeForm({ apiBase, activeTab }) {
  const toast = useToast();
  const sectionMap = {
    'cms-life-hero': { title: 'Hero Section', section: 'hero' },
    'cms-life-activities': { title: 'Activities', section: 'activities' },
    'cms-life-clubs': { title: 'Clubs & Organizations', section: 'clubs' },
    'cms-life-achievements': { title: 'Achievements', section: 'achievements' }
  };

  const sectionConfig = sectionMap[activeTab] || sectionMap['cms-life-hero'];
  const [content, setContent] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);

  useEffect(() => { loadSection(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSection = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/content?menu=student-life`, { credentials: 'include' });
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const found = result.data.find(s => s.section === sectionConfig.section);
        if (found) {
          setSectionId(found.id);
          setContent(found.content || { en: {}, id: {} });
        }
      }
    } catch (err) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (lang, fieldKey, value) => {
    setContent(prev => ({ ...prev, [lang]: { ...prev[lang], [fieldKey]: value } }));
    setIsDirty(true);
  };

  const saveSection = async () => {
    if (!sectionId) return;
    setIsSaving(true);
    try {
      await fetch(`${apiBase}/content/${sectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content })
      });
      toast.success(`✓ Saved`);
      setIsDirty(false);
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <div className="cms-loading"><div>Loading...</div></div>;
  const en = content.en || {};

  return (
    <div className="cms-form">
      <toast.ToastContainer />
      <h2>{sectionConfig.title}</h2>

      {activeTab === 'cms-life-hero' && (
        <div>
          <div className="form-group"><label>Title</label><input type="text" value={en.title || ''} onChange={(e) => updateField('en', 'title', e.target.value)} maxLength={200} /></div>
          <div className="form-group"><label>Subtitle</label><textarea value={en.subtitle || ''} onChange={(e) => updateField('en', 'subtitle', e.target.value)} rows="2" /></div>
        </div>
      )}

      {activeTab === 'cms-life-activities' && (
        <div>
          <div className="form-group"><label>Activities Overview</label><textarea value={en.overview || ''} onChange={(e) => updateField('en', 'overview', e.target.value)} rows="4" /></div>
          <h4>Activity Items</h4>
          {(en.items || []).map((item, idx) => (
            <div key={idx} style={{ border: '1px solid #ddd', padding: '10px', marginBottom: '10px', borderRadius: '4px' }}>
              <input type="text" value={item?.title || ''} onChange={(e) => { const newItems = [...(en.items || [])]; newItems[idx] = { ...newItems[idx], title: e.target.value }; updateField('en', 'items', newItems); }} placeholder="Activity name" maxLength={100} />
              <textarea value={item?.description || ''} onChange={(e) => { const newItems = [...(en.items || [])]; newItems[idx] = { ...newItems[idx], description: e.target.value }; updateField('en', 'items', newItems); }} placeholder="Description" rows="2" style={{ marginTop: '5px' }} />
              <button type="button" onClick={() => { const newItems = (en.items || []).filter((_, i) => i !== idx); updateField('en', 'items', newItems); }} style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '5px' }}>Remove</button>
            </div>
          ))}
          <button type="button" onClick={() => { const newItems = [...(en.items || []), { title: '', description: '' }]; updateField('en', 'items', newItems); }} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>+ Add Activity</button>
        </div>
      )}

      {activeTab === 'cms-life-clubs' && (
        <div>
          <div className="form-group"><label>Clubs Overview</label><textarea value={en.overview || ''} onChange={(e) => updateField('en', 'overview', e.target.value)} rows="4" /></div>
          <h4>Clubs</h4>
          {(en.items || []).map((item, idx) => (
            <div key={idx} style={{ border: '1px solid #ddd', padding: '10px', marginBottom: '10px', borderRadius: '4px' }}>
              <input type="text" value={item?.name || ''} onChange={(e) => { const newItems = [...(en.items || [])]; newItems[idx] = { ...newItems[idx], name: e.target.value }; updateField('en', 'items', newItems); }} placeholder="Club name" maxLength={100} />
              <textarea value={item?.description || ''} onChange={(e) => { const newItems = [...(en.items || [])]; newItems[idx] = { ...newItems[idx], description: e.target.value }; updateField('en', 'items', newItems); }} placeholder="Club description" rows="2" style={{ marginTop: '5px' }} />
              <button type="button" onClick={() => { const newItems = (en.items || []).filter((_, i) => i !== idx); updateField('en', 'items', newItems); }} style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '5px' }}>Remove</button>
            </div>
          ))}
          <button type="button" onClick={() => { const newItems = [...(en.items || []), { name: '', description: '' }]; updateField('en', 'items', newItems); }} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>+ Add Club</button>
        </div>
      )}

      {activeTab === 'cms-life-achievements' && (
        <div>
          <div className="form-group"><label>Achievements Overview</label><textarea value={en.overview || ''} onChange={(e) => updateField('en', 'overview', e.target.value)} rows="4" /></div>
          <h4>Student Achievements</h4>
          {(en.items || []).map((item, idx) => (
            <div key={idx} style={{ border: '1px solid #ddd', padding: '10px', marginBottom: '10px', borderRadius: '4px' }}>
              <input type="text" value={item?.title || ''} onChange={(e) => { const newItems = [...(en.items || [])]; newItems[idx] = { ...newItems[idx], title: e.target.value }; updateField('en', 'items', newItems); }} placeholder="Achievement title" maxLength={200} />
              <textarea value={item?.description || ''} onChange={(e) => { const newItems = [...(en.items || [])]; newItems[idx] = { ...newItems[idx], description: e.target.value }; updateField('en', 'items', newItems); }} placeholder="Achievement description" rows="2" style={{ marginTop: '5px' }} />
              <button type="button" onClick={() => { const newItems = (en.items || []).filter((_, i) => i !== idx); updateField('en', 'items', newItems); }} style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '5px' }}>Remove</button>
            </div>
          ))}
          <button type="button" onClick={() => { const newItems = [...(en.items || []), { title: '', description: '' }]; updateField('en', 'items', newItems); }} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>+ Add Achievement</button>
        </div>
      )}

      <div className="btn-group">
        <button onClick={saveSection} disabled={!isDirty || isSaving} className="btn-primary">{isSaving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}
